import React from 'react'
import Text from 'app/Components/twComponents/Text'
import {useHouseQuery} from 'app/_server/queries/houses'
import {HTTP404, useHistory, useParams} from 'plugins/react-router-deluxe'
import Loading from 'app/Components/Loading'
import ReservationSummary from './ReservationSummary'
import ReservationDetails from './ReservationDetails'
import ReservationSteps from './ReservationSteps'
import {_, useI18n} from 'plugins/i18n'
import Form from 'app/Components/Form'
import {useRegisterClient} from 'app/_server/queries/clients'
import {useSubscribeToNewsletter} from 'app/_server/queries/newsletter'
import {fbTrack} from 'plugins/trackers'
import {useLandingPageByUrlUserQuery} from 'app/_server/queries/landingPages'
import {useRegisterContract} from 'app/_server/queries/contracts'
import {useInitFormValue} from './hooks'
import {stringify} from 'jsurl2'

export const formId = 'reservationForm'

const Reservation = () => {
	const {lang} = useI18n()
	const {slug} = useParams()
	const history = useHistory()
	const registerClient = useRegisterClient()
	const registerContract = useRegisterContract()
	const subscribeToNewsletter = useSubscribeToNewsletter()
	const {data: dataUrl} = useLandingPageByUrlUserQuery(
		'contact/confirmation',
		lang
	)
	const {data: houseData, loading} = useHouseQuery({slug})

	const initFormValue = useInitFormValue({house: houseData?.house})

	if (!initFormValue || (!houseData && loading)) return <Loading />
	if (!houseData?.house) return <HTTP404 />

	const handleSubmit = async ({
		client: clientData,
		contract: contractData,
	}: ReservationContractFormType) => {
		const {
			email = '',
			name: {first, last} = {},
			newsletterAccept,
			tel = '',
		} = clientData
		const {options} = contractData

		try {
			const client = await registerClient({
				...clientData,
				comms: [
					{type: 'email', data: email},
					{type: 'phone', data: tel},
				],
			})
			if (newsletterAccept) {
				await subscribeToNewsletter({
					email: email!,
					firstName: first!,
					lastName: last!,
					lang,
				})
			}

			const clientId = client.data.registerClient.id
			const contractToSave: ReservationContractFormType['contract'] = {
				...contractData,
				clientId,
				options: options.map(o => ({
					...o,
					selected: undefined,
				})),
			}

			const res = await registerContract(contractToSave)

			if (res && dataUrl) {
				fbTrack('Purchase', {
					value: contractData.prices.sellTotal,
					currency: 'EUR',
				})
				const {id: contractId} = res.data.registerContract

				const url18n = dataUrl?.landingPageByUrl?.urlI18n[lang]
				const url = url18n
					? `/${lang}/${url18n}`
					: dataUrl?.landingPageByUrl?.redirectUrl || `/${lang}`
				history.replace(`${url}?${stringify({id: contractId}, {short: true})}`)
			}

			return res
		} catch (error) {
			console.error('Error in Reservation:', error)
			throw new Error('Error in Reservation')
		}
	}

	return (
		<Form<ReservationContractFormType>
			formId={formId}
			value={initFormValue}
			onChange={handleSubmit}
			withHtmlForm
			noButtons
			preventNavigation={false}
		>
			<div className="flex w-full flex-col items-start px-8 py-12">
				<Text variant="heading3XL" className="py-6">
					{_`HouseReservationLabel`}
				</Text>
				<div className="flex w-full flex-col gap-6 lg:flex-row">
					<div className="flex w-full flex-col gap-6">
						<ReservationDetails />
						<div className="w-full lg:hidden">
							<ReservationSummary />
						</div>
						<ReservationSteps />
					</div>
					<div className="hidden w-1/3 lg:block">
						<ReservationSummary />
					</div>
				</div>
			</div>
		</Form>
	)
}

export default Reservation
